import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Dropdown,
  Row,
  Col,
  Container,
  Card,
} from "react-bootstrap";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useTasks from "hooks/tasks/useTasks";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import SubtleBadge from "components/common/SubtleBadge";
import DeleteTaskModal from "./DeleteTaskModal";
import DeactivateTaskModal from "./DeactivateTaskModal";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import { toast } from "react-toastify";
import { ChevronLeft, ChevronRight } from "lucide-react"; 
import { useRef } from "react";
import { Empty, Select, Spin } from "antd";
import useBusinessEntities from "hooks/olreadia/useBusinessEntities";

const TAB_CONFIG = [
  { key: "NEW", label: "New", searchTerm: "NEW" },
  { key: "FLAGGED", label: "Flagged/Blocked", searchTerm: "BLOCKED" },
  {
    key: "IN_PROGRESS",
    label: "In Progress",
    searchTerm: "IN_PROGRESS",
  },
  { key: "COMPLETED", label: "Completed", searchTerm: "COMPLETED" },
  { key: "ACCEPTED", label: "Accepted", searchTerm: "ACCEPTED" },
  { key: "OTW", label: "OTW", searchTerm: "NAVIGATING" },
  { key: "CANCELLED", label: "Cancelled", searchTerm: "CANCELLED" },
  { key: "EXPIRED", label: "Expired", searchTerm: "EXPIRED" },
  { key: "ALL", label: "All Tasks", searchTerm: "" },
];

const STATUS_MAP = {
  NEW: { label: "New", color: "info" },
  PENDING: { label: "Pending", color: "warning" },
  ACCEPTED: { label: "Accepted", color: "success" },
  NAVIGATING: { label: "Navigating", color: "primary" },
  IN_PROGRESS: { label: "In Progress", color: "primary" },
  ARRIVED: { label: "Arrived", color: "success" },
  IN_REVIEW: { label: "In Review", color: "info" },
  COMPLETED: { label: "Completed", color: "success" },
  CANCELLED: { label: "Cancelled", color: "danger" },
  EXPIRED: { label: "Expired", color: "secondary" },
  DEACTIVATED: { label: "Deactivated", color: "secondary" },
  DELETED: { label: "Deleted", color: "dark" },
  FLAGGED: { label: "Flagged", color: "warning" },
  BLOCKED: { label: "Blocked", color: "danger" },
  OTW: { label: "OTW", color: "info" },
};

const Tasks = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { entities: businessEntities, loading: businessEntitiesLoading, list: listBusinessEntities } =
    useBusinessEntities();

  const initialBusinessEntityId = searchParams.get("businessEntityId") || "";
  const initialStatus = searchParams.get("status") || (initialBusinessEntityId ? "ALL" : "NEW");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState(
    initialStatus,
  );
  const [taskPendingDelete, setTaskPendingDelete] = useState(null);
  const [taskPendingDeactivate, setTaskPendingDeactivate] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showTabScrollControls, setShowTabScrollControls] = useState(false);
  const scrollRef = useRef(null);

  const currentTab = useMemo(
    () => TAB_CONFIG.find((tab) => tab.key === activeTab),
    [activeTab],
  );
  const currentStatusTabKey = currentTab?.key || "NEW";
  const selectedBusinessEntityId = initialBusinessEntityId;

  const selectedBusinessEntityLabel = useMemo(() => {
    if (!selectedBusinessEntityId) return "";
    const selected = (businessEntities || []).find(
      (entity) => String(entity?.id || entity?.businessEntityId || entity?.entityId || "") === String(selectedBusinessEntityId),
    );
    return selected?.name || selected?.businessName || selected?.title || selected?.ein || selectedBusinessEntityId;
  }, [businessEntities, selectedBusinessEntityId]);

  const businessEntityOptions = useMemo(
    () =>
      (businessEntities || [])
        .map((entity) => {
          const id =
            entity?.id || entity?.businessEntityId || entity?.entityId || "";
          if (!id) return null;

          const label =
            entity?.name || entity?.businessName || entity?.title || id;

          return {
            value: id,
            label,
          };
        })
        .filter(Boolean),
    [businessEntities],
  );

  const scroll = (direction) => {
  if (scrollRef.current) {
    const { scrollLeft } = scrollRef.current;
    const scrollTo = direction === "left" ? scrollLeft - 200 : scrollLeft + 200;
    scrollRef.current.scrollTo({ left: scrollTo, behavior: "smooth" });
    }
  };

  const updateTabScrollControls = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollWidth, clientWidth } = scrollRef.current;
    setShowTabScrollControls(scrollWidth > clientWidth + 1);
  }, []);


  const {
    tasks,
    loading,
    error,
    getTaskById,
    fetchTasks,
    deactivateTask,
    deleteTask,
    totalPages,
    totalElements,
  } = useTasks();

  useEffect(() => {
    listBusinessEntities({ page: 0, size: 1000 });
  }, [listBusinessEntities]);

  useEffect(() => {
    const statusFromUrl = searchParams.get("status");
    const pageFromUrl = Number(searchParams.get("page") || 0);
    const sizeFromUrl = Number(searchParams.get("size") || 10);
    const businessEntityIdFromUrl = searchParams.get("businessEntityId") || "";

    if (statusFromUrl && statusFromUrl !== activeTab) {
      setActiveTab(statusFromUrl);
    } else if (!statusFromUrl && businessEntityIdFromUrl && activeTab !== "ALL") {
      setActiveTab("ALL");
    }

    if (!Number.isNaN(pageFromUrl) && pageFromUrl !== page) {
      setPage(pageFromUrl);
    }

    if (!Number.isNaN(sizeFromUrl) && sizeFromUrl !== pageSize) {
      setPageSize(sizeFromUrl);
    }
  }, [activeTab, page, pageSize, searchParams]);

  useEffect(() => {
    updateTabScrollControls();
    window.addEventListener("resize", updateTabScrollControls);

    return () => {
      window.removeEventListener("resize", updateTabScrollControls);
    };
  }, [updateTabScrollControls]);

  useEffect(() => {
    fetchTasks(page, pageSize, currentTab?.searchTerm || "", {
      businessEntityId: selectedBusinessEntityId || undefined,
    });
  }, [page, pageSize, activeTab, currentTab?.searchTerm, fetchTasks, selectedBusinessEntityId]);

  const mappedTasks = useMemo(() => {
    return tasks.map((task) => {
      const requester = task.requester;
      const helper = task.helper;

      const requesterName = requester
        ? `${requester.firstName || ""} ${requester.lastName || ""}`
            .trim()
            .replace(/\s+/g, " ")
        : "N/A";

      const helperName = helper
        ? `${helper.firstName || ""} ${helper.lastName || ""}`
            .trim()
            .replace(/\s+/g, " ")
        : "N/A";

      return {
        id: task.id,
        title: task.title,
        requesterName,
        helperName,
        status: task.status,
        rawTask: task,
      };
    });
  }, [tasks]);

  const formatText = (text) =>
    text && text.length > 15 ? `${text.slice(0, 15)}...` : text;

  const triggerToast = useCallback((message, variant = "success") => {
    toast(message || (variant === "success" ? "Success" : "Error"), {
      type: variant,
      autoClose: 3500,
      closeButton: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, []);

  const handleViewTask = useCallback(
    async (id) => {
      try {
        await getTaskById(id);
        navigate(`/olitask/tasks/${id}`, {
          state: {
            businessEntityId: selectedBusinessEntityId || undefined,
            status: currentStatusTabKey,
            page,
            pageSize,
          },
        });
      } catch (err) {
        triggerToast("Failed to fetch task details", "error");
      }
    },
    [
      getTaskById,
      navigate,
      triggerToast,
      selectedBusinessEntityId,
      currentStatusTabKey,
      page,
      pageSize,
    ],
  );

  const handleDeleteRequest = useCallback((task) => {
    setTaskPendingDelete(task);
  }, []);

  const handleCloseDeleteModal = useCallback(
    () => setTaskPendingDelete(null),
    [],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!taskPendingDelete) return;
    try {
      setProcessing(true);
      await deleteTask(taskPendingDelete.rawTask.id, page, pageSize, currentTab?.searchTerm || "", {
        businessEntityId: selectedBusinessEntityId || undefined,
      });
      triggerToast("Task deleted successfully", "success");
      handleCloseDeleteModal();
    } catch (err) {
      triggerToast(err?.message || "Failed to delete task", "error");
    } finally {
      setProcessing(false);
    }
  }, [
    deleteTask,
    taskPendingDelete,
    triggerToast,
    handleCloseDeleteModal,
    page,
    pageSize,
    currentTab,
    selectedBusinessEntityId,
  ]);

  const handleDeactivateRequest = useCallback((task) => {
    setTaskPendingDeactivate(task);
  }, []);

  const handleCloseDeactivateModal = useCallback(
    () => setTaskPendingDeactivate(null),
    [],
  );

  const handleConfirmDeactivate = useCallback(async () => {
    if (!taskPendingDeactivate) return;
    try {
      setProcessing(true);
      await deactivateTask(taskPendingDeactivate.rawTask.id, page, pageSize, currentTab?.searchTerm || "", {
        businessEntityId: selectedBusinessEntityId || undefined,
      });
      triggerToast("Task deactivated successfully", "success");
      handleCloseDeactivateModal();
    } catch (err) {
      triggerToast(err?.message || "Failed to deactivate task", "error");
    } finally {
      setProcessing(false);
    }
  }, [
    deactivateTask,
    taskPendingDeactivate,
    triggerToast,
    handleCloseDeactivateModal,
    page,
    pageSize,
    currentTab,
    selectedBusinessEntityId,
  ]);

  useEffect(() => {
    setPage(0);
  }, [selectedBusinessEntityId]);

  const handleTabChange = useCallback((tabKey) => {
    setPage(0);
    setActiveTab(tabKey);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("status", tabKey);
    nextParams.set("page", "0");
    nextParams.set("size", String(pageSize));
    if (selectedBusinessEntityId) {
      nextParams.set("businessEntityId", selectedBusinessEntityId);
    } else {
      nextParams.delete("businessEntityId");
    }
    setSearchParams(nextParams, { replace: true });
  }, [pageSize, searchParams, selectedBusinessEntityId, setSearchParams]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", String(newPage));
    nextParams.set("size", String(pageSize));
    nextParams.set("status", currentStatusTabKey);
    if (selectedBusinessEntityId) {
      nextParams.set("businessEntityId", selectedBusinessEntityId);
    } else {
      nextParams.delete("businessEntityId");
    }
    setSearchParams(nextParams, { replace: true });
  }, [currentStatusTabKey, pageSize, searchParams, selectedBusinessEntityId, setSearchParams]);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setPage(0);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", "0");
    nextParams.set("size", String(size));
    nextParams.set("status", currentStatusTabKey);
    if (selectedBusinessEntityId) {
      nextParams.set("businessEntityId", selectedBusinessEntityId);
    } else {
      nextParams.delete("businessEntityId");
    }
    setSearchParams(nextParams, { replace: true });
  }, [currentStatusTabKey, searchParams, selectedBusinessEntityId, setSearchParams]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Task ID",
        cell: ({ getValue }) => getValue(),
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ getValue }) => formatText(getValue()),
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "requesterName",
        header: "Requester",
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "helperName",
        header: "Helper",
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "status",
        header: "Status",
        meta: {
          headerProps: { className: "text-900" },
          cellProps: { className: "fs-9" },
        },
        cell: ({ getValue }) => {
          const statusRaw = getValue() || "UNKNOWN";
          const { label, color } = STATUS_MAP[statusRaw] || {
            label: statusRaw,
            color: "secondary",
          };
          return (
            <SubtleBadge pill bg={color}>
              {label}
            </SubtleBadge>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        meta: { headerProps: { className: "text-900" } },
        cell: ({ row }) => {
          const item = row.original;
          return (
            <Dropdown className="action-dropdown p-0" onClick={(event) => event.stopPropagation()}>
              <Dropdown.Toggle
                as="button"
                className="bg-transparent border-0 p-0 shadow-none text-secondary"
              >
                ⋯
              </Dropdown.Toggle>
              <Dropdown.Menu className="action-menu">
            <Dropdown.Item onClick={() => handleViewTask(item.id)}>
                  View
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={() => handleDeactivateRequest(item)}
                  disabled={processing}
                >
                  Deactivate
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => handleDeleteRequest(item)}
                  disabled={processing}
                >
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          );
        },
      },
    ],
    [handleDeactivateRequest, handleDeleteRequest, handleViewTask, processing],
  );

  const table = useAdvanceTable({
    data: mappedTasks,
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  if (error) {
    return (
      <Container>
        <div className="alert alert-danger d-flex align-items-center justify-content-between">
          <div>Error loading tasks: {error}</div>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() =>
              fetchTasks(page, pageSize, currentTab?.searchTerm || "", {
                businessEntityId: selectedBusinessEntityId || undefined,
              })
            }
          >
            Retry
          </button>
        </div>
      </Container>
    );
  }

  const hasTasks = tasks.length > 0;

  return (
    <>
      <WidgetSectionTitle
        icon="poll"
        title="Task Management"
        subtitle="Manage and oversee all tasks within the OliTask platform."
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      <Row className="mb-3 align-items-center pt-1">
        <Col className="d-flex align-items-center gap-2">
          {showTabScrollControls ? (
            <button
              className="btn btn-link p-1 text-dark border-0"
              onClick={() => scroll("left")}
              style={{ zIndex: 10 }}
              aria-label="Scroll status filters left"
            >
              <ChevronLeft size={20} />
            </button>
          ) : null}

          <div
            ref={scrollRef}
            style={{
              overflowX: "auto",
              whiteSpace: "nowrap",
              scrollbarWidth: "none", // Hides scrollbar on Firefox
              msOverflowStyle: "none", // Hides scrollbar on IE/Edge
            }}
            className="flex-grow-1 no-scrollbar"
          >
            <div className="d-inline-flex align-items-center gap-2 py-1">
              {TAB_CONFIG.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleTabChange(key)}
                  className={`btn btn-sm rounded-pill px-3 py-2 border ${
                    activeTab === key
                      ? "btn-primary border-primary text-white shadow-sm"
                      : "btn-light border-200 text-secondary"
                  }`}
                  style={{
                    whiteSpace: "nowrap",
                    minHeight: 36,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {showTabScrollControls ? (
            <button
              className="btn btn-link p-1 text-dark border-0"
              onClick={() => scroll("right")}
              style={{ zIndex: 10 }}
              aria-label="Scroll status filters right"
            >
              <ChevronRight size={20} />
            </button>
          ) : null}
        </Col>
      </Row>

      <AdvanceTableProvider {...table}>
        <Row className="mb-2 g-2 align-items-start pt-1">
          <Col md={4}>
            <AdvanceTableSearchBox />
          </Col>
          <Col
            xs={12}
            lg="auto"
            className="ms-lg-auto d-flex flex-column align-items-lg-end gap-1"
            style={{ minWidth: 240 }}
          >
            <Select
              showSearch
              allowClear
              size="middle"
              placeholder="Business entity"
              value={selectedBusinessEntityId || undefined}
              options={businessEntityOptions}
              optionFilterProp="label"
              onChange={(nextBusinessEntityId) => {
                setPage(0);
                const nextParams = new URLSearchParams(searchParams);
                if (nextBusinessEntityId) {
                  nextParams.set("businessEntityId", nextBusinessEntityId);
                } else {
                  nextParams.delete("businessEntityId");
                }
                nextParams.set("status", currentStatusTabKey);
                nextParams.set("page", "0");
                nextParams.set("size", String(pageSize));
                setSearchParams(nextParams, { replace: true });
              }}
              loading={businessEntitiesLoading}
              style={{ width: "100%", maxWidth: 250 }}
              className="text-start"
            />
          </Col>
        </Row>

        {loading && !hasTasks ? (
          <Card className="mt-3 custom-shadow">
            <Card.Body className="text-center py-5">
              <Spin size="small" />
              <div className="mt-3 text-sm text-muted">Loading tasks…</div>
            </Card.Body>
          </Card>
        ) : hasTasks ? (
          <div className="position-relative">
            {loading ? (
              <div
                className="position-absolute top-0 end-0 mt-2 me-2 d-inline-flex align-items-center gap-2 rounded-pill border bg-white px-3 py-1 shadow-sm"
                style={{ zIndex: 3 }}
              >
                <Spin size="small" />
              </div>
            ) : null}
            <AdvanceTable
              headerClassName="bg-200 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              rowProps={(row) => ({
                onClick: () => handleViewTask(row.original.id),
                style: { cursor: "pointer" },
              })}
              tableProps={{
                bordered: true,
                striped: true,
                className: "fs-10 mb-0 overflow-hidden",
              }}
            />

            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                onPrev={() => handlePageChange(Math.max(page - 1, 0))}
                onNext={() =>
                  handlePageChange(Math.min(page + 1, totalPages - 1))
                }
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 20, 30, 50, 100, 200, 500]}
              />
            </div>
          </div>
        ) : (
          <Card className="mt-3 custom-shadow">
            <Card.Body className="text-center py-5">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <h5 className="text-muted mb-2 text-sm">
                      {activeTab === "ALL"
                        ? "No tasks found"
                        : `No ${currentTab?.label.toLowerCase()} tasks`}
                    </h5>
                    <p className="text-muted mb-0 text-xs">
                      {activeTab === "ALL"
                        ? selectedBusinessEntityId
                          ? `There are no tasks for business entity "${selectedBusinessEntityLabel}".`
                          : "There are no tasks in the system yet."
                        : selectedBusinessEntityId
                          ? `There are no ${currentTab?.label.toLowerCase()} tasks for business entity "${selectedBusinessEntityLabel}".`
                          : `There are no tasks with status "${currentTab?.label}" at the moment.`}
                    </p>
                  </div>
                }
              />
            </Card.Body>
          </Card>
        )}
      </AdvanceTableProvider>

      <DeleteTaskModal
        show={Boolean(taskPendingDelete)}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        task={taskPendingDelete?.rawTask}
        processing={processing}
      />

      <DeactivateTaskModal
        show={Boolean(taskPendingDeactivate)}
        onClose={handleCloseDeactivateModal}
        onConfirm={handleConfirmDeactivate}
        task={taskPendingDeactivate?.rawTask}
        processing={processing}
      />
    </>
  );
};

export default Tasks;
